import React, { useState } from "react";
import {
  TextField,
  FormControl,
  InputLabel,
  OutlinedInput,
  InputAdornment,
  IconButton,
  Alert,
  Button,
  CircularProgress,
  Container,
  Typography,
} from "@mui/material";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { encryptPassword } from "../../../utils/Encryption";
import { setCookie } from "../../../utils/CookiesUtil";
import { ROUTES_CONSTANTS } from "../../../constants/routesConstants.js";
import { AUTH_MESSAGES } from "../../../constants/messagesConstants";
import {
  setIsAuth,
  setAccessToken,
} from "../../../store/reducers/auth/authReducer";
import {
  setExtraInformation,
  setUserInformation,
  setPermissions,
} from "../../../store/reducers/user/userReducer.js";
import { login } from "../../../api/authApi";
import styles from "./loginFormStyles";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";

const LoginForm = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");

  const handleEmailChange = (event) => setEmail(event.target.value);
  const handlePasswordChange = (event) => setPassword(event.target.value);
  const handleClickShowPassword = (event) => setShowPassword(!showPassword);
  const handleMouseDownPassword = (event) => event.preventDefault();
  const handleClickCloseAlert = (event) => setError("");

  const validateForm = (email, password) => {
    if (!email && !password) {
      setError("Por favor, ingresa tu correo electrónico y contraseña.");
      return false;
    }

    if (!email) {
      setError("Por favor, ingresa tu correo electrónico.");
      return false;
    }

    if (!password) {
      setError("Por favor, ingresa tu contraseña.");
      return false;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError("El correo electrónico no es válido.");
      return false;
    }

    setError("");
    return true;
  };

  const prepareData = () => {
    setEmail(email.trim());
    setPassword(password.trim());

    setEmail(email.toLocaleLowerCase());
    // La contraseña no debe convertirse a minúsculas para mantener su sensibilidad a mayúsculas/minúsculas
  };

  const handleClickLogin = async () => {
    const isFormValid = validateForm(email, password);

    if (!isFormValid) {
      return;
    }

    prepareData();

    setIsLoading(true);

    const payload = {
      new_user_email: email,
      new_user_password: encryptPassword(password),
      enviroment: "admin",
    };

    login(payload)
      .then((response) => {
        if (
          response.data.status_Code === 200 &&
          response.data.status_Message === AUTH_MESSAGES.VALID_USER
        ) {
          const accessToken = response.data.body.auth_token;
          const permissions = response.data.body.permissions || [];
          const userInformation = response.data.user_info;
          const extraInformation = response.data.body;

          setCookie("accessToken", accessToken);

          dispatch(setAccessToken({ accessToken: accessToken }));
          dispatch(setPermissions({ permissions: permissions }));
          dispatch(setIsAuth({ isAuth: accessToken }));
          dispatch(setUserInformation({ userInformation: userInformation }));
          dispatch(setExtraInformation({ extraInformation: extraInformation }));

          setIsLoading(false);

          navigate(ROUTES_CONSTANTS.DASHBOARD);
        } else {
          setIsLoading(false);
          setError(response.data.status_Message);
        }
      })
      .catch((error) => {
        setIsLoading(false);
        setError(`${error || 'Error desconocido'}`);
      });
  };

  return (
    <Container style={styles.container}>
      <TextField
        id="outlined-basic"
        label="Correo"
        variant="outlined"
        value={email}
        onChange={handleEmailChange}
        style={styles.textfield}
      />
      <FormControl style={styles.form} variant="outlined">
        <InputLabel htmlFor="outlined-adornment-password">
          Contraseña
        </InputLabel>
        <OutlinedInput
          id="outlined-adornment-password"
          type={showPassword ? "text" : "password"}
          value={password}
          onChange={handlePasswordChange}
          endAdornment={
            <InputAdornment position="end">
              <IconButton
                aria-label="toggle password visibility"
                onClick={handleClickShowPassword}
                onMouseDown={handleMouseDownPassword}
                edge="end"
              >
                {showPassword ? <VisibilityOff /> : <Visibility />}
              </IconButton>
            </InputAdornment>
          }
          label="Contraseña"
        />
      </FormControl>
      {error && (
        <Alert
          severity="error"
          onClose={handleClickCloseAlert}
          style={styles.alert}
        >
          {error}
        </Alert>
      )}
      <Button
        variant="contained"
        style={styles.button}
        onClick={handleClickLogin}
      >
        {isLoading ? (
          <CircularProgress size="24px" style={styles.circularProgress} />
        ) : (
          <Typography variant="body1">Iniciar sesión</Typography>
        )}
      </Button>
    </Container>
  );
};

export default LoginForm;
